import { Check, Laptop, Loader2, MonitorSmartphone, RadioTower, RotateCw, X } from "lucide-react"
import type { ReactNode } from "react"
import type { NestConnectionFailure, NestConnectionFailureStage } from "../../nest/connectionFailure"

type PathState = "connected" | "failed" | "waiting"

interface ConnectionCopy {
    title: string
    detail: string
    accessiblePath: string
}

function connectionCopy(computerName: string, stage: NestConnectionFailureStage | undefined): ConnectionCopy {
    switch (stage) {
        case "device-to-relay":
            return {
                title: "Can't reach the private relay",
                detail: `Check this device's internet connection. ${computerName} may still be online.`,
                accessiblePath: `Connection path: this device cannot reach the private relay; ${computerName} Nest status is unknown.`,
            }
        case "relay":
            return {
                title: "The private relay is unavailable",
                detail: `This device reached the relay, but it cannot accept the connection right now. ${computerName} may still be online.`,
                accessiblePath: `Connection path: this device reached the private relay; the relay is unavailable; ${computerName} Nest status is unknown.`,
            }
        case "relay-to-nest":
            return {
                title: `${computerName} is offline`,
                detail: `This device reached the private relay, but the relay can't reach ${computerName}. Wake the computer and make sure WildLark is running.`,
                accessiblePath: `Connection path: this device reached the private relay; the relay cannot reach ${computerName} Nest.`,
            }
        case "device-to-nest":
            return {
                title: `Can't reach ${computerName}`,
                detail: `The direct connection to ${computerName} is unavailable. Make sure the computer and its private network are online.`,
                accessiblePath: `Connection path: this device cannot reach ${computerName} Nest directly.`,
            }
        default:
            return {
                title: `Reconnecting to ${computerName}`,
                detail: "The connection path is still being checked. WildLark will identify the unavailable step when the next attempt completes.",
                accessiblePath: `Connection path to ${computerName} Nest is being checked.`,
            }
    }
}

function PathNode({ icon, label, state }: { icon: ReactNode; label: string; state: PathState }) {
    const tone =
        state === "connected"
            ? "border-success/50 bg-success/10 text-success"
            : state === "failed"
              ? "border-error/50 bg-error/10 text-error"
              : "border-border bg-base-200 text-muted"
    return (
        <div className="flex w-20 flex-none flex-col items-center gap-2 text-center">
            <div className={`relative flex h-12 w-12 items-center justify-center border ${tone}`}>
                {icon}
                {state !== "waiting" && (
                    <span
                        className={`absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full ${state === "connected" ? "bg-success text-success-content" : "bg-error text-error-content"}`}
                    >
                        {state === "connected" ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <X className="h-2.5 w-2.5" strokeWidth={3} />}
                    </span>
                )}
            </div>
            <span
                className={`text-[11px] font-medium leading-4 ${state === "failed" ? "text-error" : state === "connected" ? "text-base-content" : "text-muted"}`}
            >
                {label}
            </span>
        </div>
    )
}

function PathLink({ state }: { state: PathState }) {
    return (
        <div className="relative mt-6 h-px min-w-7 flex-1" aria-hidden="true">
            <div className={`absolute inset-0 ${state === "connected" ? "bg-success/60" : state === "failed" ? "bg-error/70" : "bg-border"}`} />
            {state === "failed" && <X className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 bg-base-100 p-0.5 text-error" />}
        </div>
    )
}

function ConnectionPath({ computerName, failure }: { computerName: string; failure: NestConnectionFailure | null }) {
    const stage = failure?.stage
    if (stage === "device-to-nest") {
        return (
            <div className="mx-auto flex w-full max-w-xs items-start justify-center" role="img" aria-label={connectionCopy(computerName, stage).accessiblePath}>
                <PathNode icon={<MonitorSmartphone className="h-5 w-5" />} label="This device" state="connected" />
                <PathLink state="failed" />
                <PathNode icon={<Laptop className="h-5 w-5" />} label="Nest" state="failed" />
            </div>
        )
    }

    const relayState: PathState = stage === "device-to-relay" || stage === "relay" ? "failed" : stage === "relay-to-nest" ? "connected" : "waiting"
    const nestState: PathState = stage === "relay-to-nest" ? "failed" : "waiting"
    return (
        <div className="mx-auto flex w-full max-w-md items-start justify-center" role="img" aria-label={connectionCopy(computerName, stage).accessiblePath}>
            <PathNode icon={<MonitorSmartphone className="h-5 w-5" />} label="This device" state="connected" />
            <PathLink state={stage === "device-to-relay" ? "failed" : stage ? "connected" : "waiting"} />
            <PathNode icon={<RadioTower className="h-5 w-5" />} label="Private relay" state={relayState} />
            <PathLink state={stage === "relay-to-nest" ? "failed" : "waiting"} />
            <PathNode icon={<Laptop className="h-5 w-5" />} label="Nest" state={nestState} />
        </div>
    )
}

export function ConnectionIssueView({
    computerName,
    failure,
    retrying,
    busy = false,
    onRetry,
    onForget,
}: {
    computerName: string
    failure: NestConnectionFailure | null
    retrying: boolean
    busy?: boolean
    onRetry?: () => void
    onForget: () => void
}) {
    const copy = connectionCopy(computerName, failure?.stage)
    return (
        <div className="code-theme code-theme-black flex min-h-screen w-screen items-center justify-center bg-base-100 p-5 text-base-content">
            <main className="w-full max-w-xl text-center">
                <div className="border border-border bg-base-200/30 px-3 py-6 sm:px-6">
                    <ConnectionPath computerName={computerName} failure={failure} />
                </div>
                <h1 className="mt-7 text-xl font-semibold tracking-tight">{copy.title}</h1>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted">{copy.detail}</p>
                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted" role="status">
                    {retrying || busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <RotateCw className="h-3.5 w-3.5 text-primary" />}
                    {retrying ? "Retrying automatically" : "Ready to try again"}
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                    {onRetry && (
                        <button
                            type="button"
                            className="btn flex h-9 items-center gap-2 bg-primary px-3 text-sm text-primary-content hover:bg-primary/90 disabled:opacity-60"
                            disabled={busy}
                            onClick={onRetry}
                        >
                            <RotateCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                            Try again
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn h-9 border border-border px-3 text-sm text-muted hover:bg-base-200 hover:text-base-content"
                        onClick={onForget}
                    >
                        Forget this computer
                    </button>
                </div>
            </main>
        </div>
    )
}
